/**
 * 🚀 Fase 2.1 — Lazy load do sipService.
 *
 * sipService importa sip.js (~150KB minified+gzipped). Carregar essa lib no
 * bundle inicial atrasa o time-to-interactive de TODOS os usuários, mesmo
 * aqueles que não usam softphone (CRM-only, suporte sem ramal, etc.).
 *
 * Este módulo expõe APIs que carregam o sipService sob demanda via dynamic
 * import (webpack code splitting), gerando um chunk separado "sip-service".
 *
 * Estratégia:
 *  - `loadSipService()`: async, garante carregamento e retorna a instância.
 *  - `peekSipService()`: sync, retorna a instância se já carregada, ou null.
 *  - `TransferState`: re-exportado estaticamente (constants apenas, não puxa SIP.js).
 *
 * O carregamento real acontece apenas quando o user inicia a conexão SIP
 * (via VoipContext.connect), evitando incluir SIP.js no main bundle.
 */

let _instance = null;
let _loadingPromise = null;

/**
 * TransferState — duplicado aqui para evitar carregar sipService só por causa
 * dos enums. Mantenha em sincronia com sipService.js.
 */
export const TransferState = Object.freeze({
  IDLE: "idle",
  INITIATING: "initiating",
  CONSULTING: "consulting",
  CONSULT_RINGING: "consult_ringing",
  CONSULT_CONNECTED: "consult_connected",
  COMPLETING: "completing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
});

/**
 * Carrega o sipService de forma assíncrona (dynamic import).
 * Retorna a mesma instância em chamadas subsequentes.
 *
 * @returns {Promise<import("./sipService").default>}
 */
export async function loadSipService() {
  if (_instance) return _instance;
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = import(
    /* webpackChunkName: "sip-service" */
    /* webpackPrefetch: true */
    "./sipService"
  )
    .then((mod) => {
      _instance = mod.default;
      return _instance;
    })
    .catch((err) => {
      _loadingPromise = null;
      throw err;
    });

  return _loadingPromise;
}

/**
 * Retorna o sipService se já tiver sido carregado, ou null.
 * Útil para callbacks síncronos (cleanup, setVolume, etc.) que não devem
 * disparar o carregamento se ainda não aconteceu.
 *
 * @returns {import("./sipService").default | null}
 */
export function peekSipService() {
  return _instance;
}

/**
 * Indica se o sipService já está carregado em memória.
 */
export function isSipServiceLoaded() {
  return _instance !== null;
}

/**
 * Pré-carrega o sipService em background sem bloquear a UI.
 * Útil para warming após login, quando o usuário tem voipExtension configurado.
 */
export function prefetchSipService() {
  if (_instance || _loadingPromise) return;
  // fire and forget
  loadSipService().catch(() => {
    // erro silencioso — o connect() real vai re-tentar e tratar
  });
}
